import React, { useState } from "react";

import { Close } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Chip, InputAdornment, TextField, Autocomplete, IconButton, Button } from "@mui/material";
import InfoDialog from "components/InfoDialog";
import useEventState from "hooks/useEventstate";
import generateAI from "img/generative-ai.svg";
import { useSearchParams } from "react-router-dom";
import { useDebounceCallback } from "usehooks-ts";
import { publish } from "utils/events";

import { COMMENTS_APP_DATA_LOADING_EVENT, COMMENTS_APP_FILTERS_APPLIED_EVENT, COMMENTS_APP_FILTERS_CANCEL_EVENT } from "../constants";

const suggestedComments = [
  "Culture",
  "One orbit",
  "One autodesk",
  "Optimistic", 
  "Relentless", 
  "Brave", 
  "Ingenious", 
  "Trusted", 
  "Belonging", 
  "Care", 
  "Collaboration", 
  "Decision Speed", 
  "Flexible Work", 
  "Productivity",
  "AI"
];

const SearchFilter = ({ searchQuery, dateFilters, isLoading }) => {
  const [value, setValue] = useState(() => searchQuery || "");
  const [currentParams, setCurrentParam] = useSearchParams();
  const isDataLoading = useEventState(COMMENTS_APP_DATA_LOADING_EVENT);

  const handleFilterChange = (name, value) => {
    if(!value) {
      currentParams.delete(name);
    } else {
      if (Array.isArray(value)) {
        value = value.join("_");
      }
      currentParams.set(name, value);
    }
    setCurrentParam(currentParams);
  };

  const handleSearchChange = (value, immediate) => {
    setValue(value);
    if (immediate) {
      if (value) {
        currentParams.set("search", value);
      } else {
        currentParams.delete("search");
      }
      setCurrentParam(currentParams);
      handleApplyFilters();
    } else {
      handleSearch(value);
    }
  };

  const handleSearch = useDebounceCallback((search) => {
    if (search || value) {
      currentParams.set("search", search || value);
    } else {
      currentParams.delete("search");
    }
    setCurrentParam(currentParams);
  }, 500);

  const handleApplyFilters = () => {
    publish(COMMENTS_APP_FILTERS_APPLIED_EVENT);
  };

  const handleCancelFilters = () => {
    publish(COMMENTS_APP_FILTERS_CANCEL_EVENT);
  };

  const filters = [
    {
      label: "Favorability",
      name: "favorability",
      options: dateFilters?.favorability ?? [],
      //value: getValues(currentParams, "favorability"),
      value: currentParams.get("favorability")
    },
    {
      label: "Question",
      name: "question",
      options: dateFilters?.question ?? [],
      value: currentParams.get("question")?.split("_").filter(Boolean) || []
    },
    {
      label: "Sentiment",
      name: "sentiment",
      options: dateFilters?.sentiment ?? ["positive", "negative", "neutral"],
      value: currentParams.get("sentiment")
    },
    {
      label: "Tenure",
      name: "tenure",
      options: dateFilters?.tenure ?? [],
      value: currentParams.get("tenure")
    }
  ];

  return (
    <div>
      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          value={value}
          type="text"
          onChange={(e) => { handleSearchChange(e.target.value); }}
          placeholder="Search for a topic or a phrase"
          sx={{ width: "500px", borderRadius: "5px" }}
          size="small"
          disabled={isLoading || isDataLoading}
          InputProps={{
            sx: {
              borderRadius: "5px"
            },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {value && (
                  <IconButton
                    size="small"
                    onClick={() => handleSearchChange("", true)}
                  >
                    <Close />
                  </IconButton>
                )}
                <img src={generateAI} alt="ai" />
              </InputAdornment>
            )
          }}
        />
        <div className="flex items-center gap-2 ml-auto">
          {
            filters.map((filter) => (
              <Autocomplete
                key={filter.name}
                multiple={filter.name === "question"}
                options={filter.options}
                getOptionLabel={(option) => option}
                value={filter.value}
                size="small"
                disabled={isLoading || isDataLoading}
                sx={{ minWidth: 200 }}
                onChange={(_e, values)=>handleFilterChange(filter.name, values)}
                ChipProps={{
                  variant: "contained",
                  color: "primary",
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={filter.label}
                    placeholder={filter.label}
                  />
                )}
              />
            ))
          }

          {
            isDataLoading ? 
              <Button variant="contained" color="primary" onClick={handleCancelFilters} disabled={isLoading}>
                Cancel
              </Button> : <Button variant="contained" color="primary" onClick={handleApplyFilters} disabled={isLoading || isDataLoading}>
                Apply Filters
              </Button>
          }

        </div>
      </Box>
      <span className="text-sm flex gap-2 text-[#6b7280] mt-1">
      Customize the narratives by using the filters or search bar.
        <InfoDialog 
          triggerComponent={<span className="text-sm underline cursor-pointer"> Learn more</span>}
          title={"Search Topics"}
          description="Enter the topic you would to understand better, or choose from one of the pre built topics below.  The application will search through comments to build narrative themes based on your search"
        />
      </span>
      <Box pt={1}>
        {suggestedComments.map((comment, index) => (
          <Chip 
            variant={value === comment ? "contained" : "outlined"}
            key={index} 
            label={comment} 
            color="primary"
            sx={{ m: 0.5 }}
            disabled={isLoading || isDataLoading}
            onClick={() => handleSearchChange(comment, true)}
          />
        ))}
      </Box>
    </div>
  );
};

export default SearchFilter;
